/**
 * Org Detail / Plan & Billing tab.
 *
 * Three sections, fetched in parallel:
 *  - Plan summary (from the org already in Outlet context)
 *  - Stripe subscription state (GET /api/billing/organizations/:id/subscription)
 *  - Recent invoices (GET /api/billing/organisations/:id/invoices)
 *
 * Subscription fetch can fail when the org isn't linked to Stripe yet
 * (no stripeCustomerId / stripeSubscriptionId) — that's a soft state, not
 * an error. Show "Not linked" and link to the org list for plan assignment.
 *
 * Mutations (cancel, change plan, archive) deliberately stay in the
 * legacy slide-over modal for now. 53c.5 polish moves them into this tab.
 */
import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingSkeleton } from '@repro/ui';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { api } from '../../../utils/api';
import type { StripeSubscription } from '../../../types';
import type { Invoice } from '../../../shared';
import type { OrgDetailContext } from '../OrgDetail';

const INVOICE_PREVIEW_COUNT = 5;

export default function Billing() {
  const { organisation } = useOutletContext<OrgDetailContext>();

  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [subscriptionMissing, setSubscriptionMissing] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSubscriptionLoading(true);
    setSubscriptionMissing(false);

    if (!organisation.stripeSubscriptionId) {
      setSubscription(null);
      setSubscriptionMissing(true);
      setSubscriptionLoading(false);
    } else {
      api
        .getSubscription(organisation.id)
        .then((res) => {
          if (!cancelled) setSubscription(res.data);
        })
        .catch(() => {
          if (!cancelled) setSubscriptionMissing(true);
        })
        .finally(() => {
          if (!cancelled) setSubscriptionLoading(false);
        });
    }

    setInvoicesLoading(true);
    setInvoicesError(null);
    api
      .getInvoices(organisation.id)
      .then((data) => {
        if (!cancelled) setInvoices(data ?? []);
      })
      .catch((err) => {
        if (!cancelled)
          setInvoicesError(err instanceof Error ? err.message : 'Failed to load invoices');
      })
      .finally(() => {
        if (!cancelled) setInvoicesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organisation.id, organisation.stripeSubscriptionId]);

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Plan + Stripe subscription */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/60 mb-3">
          Subscription
        </h2>
        {subscriptionLoading ? (
          <LoadingSkeleton rows={4} />
        ) : subscriptionMissing || !subscription ? (
          <p className="text-sm text-base-content/70">
            {organisation.stripeSubscriptionId
              ? 'Stripe subscription unreachable — try again in a moment.'
              : 'Not linked to Stripe yet.'}
          </p>
        ) : (
          <dl className="space-y-2">
            <Row
              label="Status"
              value={
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${subscriptionStatusClass(
                    subscription.status
                  )}`}
                >
                  {subscription.status}
                </span>
              }
            />
            <Row
              label="Subscription ID"
              value={<code className="text-xs">{subscription.id}</code>}
            />
            <Row
              label="Customer ID"
              value={<code className="text-xs">{subscription.customer}</code>}
            />
            <Row
              label="Period"
              value={`${formatTimestamp(subscription.current_period_start)} → ${formatTimestamp(
                subscription.current_period_end
              )}`}
            />
            <Row
              label="Cancel at period end"
              value={subscription.cancel_at_period_end ? 'Yes' : 'No'}
            />
            {subscription.canceled_at && (
              <Row label="Canceled" value={formatTimestamp(subscription.canceled_at)} />
            )}
          </dl>
        )}
      </div>

      {/* Plan summary */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/60 mb-3">
          Plan
        </h2>
        <dl className="space-y-2">
          <Row label="Plan ID" value={<code className="text-xs">{organisation.planId}</code>} />
          {organisation.billingMethod && (
            <Row label="Billing method" value={organisation.billingMethod} />
          )}
          {organisation.planStatus && <Row label="Plan status" value={organisation.planStatus} />}
        </dl>
        <p className="mt-4 text-xs text-base-content/60">
          Plan change controls live in the slide-over panel from the org list. They'll move into
          this tab in 53c.5.
        </p>
      </div>

      {/* Invoices */}
      <div className="bg-base-100 shadow rounded-lg p-6 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/60">
            Recent invoices
          </h2>
          <Link to={`/invoices/${organisation.id}`} className="btn btn-ghost btn-xs gap-1">
            <span>All invoices</span>
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </Link>
        </div>
        {invoicesLoading ? (
          <LoadingSkeleton rows={3} />
        ) : invoicesError ? (
          <ErrorState title="Couldn't load invoices" body={invoicesError} />
        ) : invoices.length === 0 ? (
          <EmptyState title="No invoices" body="No invoices have been issued for this org yet." />
        ) : (
          <table className="table w-full">
            <thead>
              <tr>
                <th>Number</th>
                <th>Issue date</th>
                <th>Due date</th>
                <th>Status</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, INVOICE_PREVIEW_COUNT).map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Link
                      to={`/invoices/${organisation.id}/${invoice.id}`}
                      className="link link-hover font-medium"
                    >
                      {invoice.invoiceNumber}
                    </Link>
                  </td>
                  <td className="text-sm text-base-content/70">{formatDate(invoice.issueDate)}</td>
                  <td className="text-sm text-base-content/70">{formatDate(invoice.dueDate)}</td>
                  <td>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${invoiceStatusClass(
                        invoice.status
                      )}`}
                    >
                      {invoice.status}
                    </span>
                  </td>
                  <td className="text-right text-sm">
                    {formatMoney(invoice.total, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {invoices.length > INVOICE_PREVIEW_COUNT && (
          <p className="mt-3 text-xs text-base-content/60">
            Showing {INVOICE_PREVIEW_COUNT} of {invoices.length} invoices.{' '}
            <Link to={`/invoices/${organisation.id}`} className="link">
              See all
            </Link>
            .
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-sm text-base-content/70">{label}</dt>
      <dd className="text-sm text-base-content text-right break-all">{value}</dd>
    </div>
  );
}

function subscriptionStatusClass(status: StripeSubscription['status']): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-green-100 text-green-800';
    case 'past_due':
    case 'unpaid':
      return 'bg-red-100 text-red-800';
    case 'canceled':
    case 'incomplete_expired':
      return 'bg-base-200 text-base-content/70';
    case 'incomplete':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-base-200 text-base-content/70';
  }
}

function invoiceStatusClass(status: string): string {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'sent':
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'void':
    case 'canceled':
    case 'cancelled':
      return 'bg-base-200 text-base-content/70';
    case 'draft':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-base-200 text-base-content/70';
  }
}

function formatTimestamp(seconds: number): string {
  return new Date(seconds * 1000).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDate(value: Date | string | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(value);
  }
}

function formatMoney(amountMinor: number, currency: string): string {
  const symbols: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };
  const symbol = symbols[currency.toUpperCase()] ?? currency;
  return `${symbol}${(amountMinor / 100).toFixed(2)}`;
}
